import { X, Check } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";

export interface CustomFilters {
  brands: string[];
  conditions: string[];
  priceMin: string;
  priceMax: string;
  locations: string[];
}

interface CustomFilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  customFilters: CustomFilters;
  onCustomFilterChange: (filters: CustomFilters) => void;
  activeCustomFiltersCount: number;
  onClearCustomFilters: () => void;
}

const AVAILABLE_BRANDS = [
  "Apple",
  "Samsung",
  "Nike",
  "Adidas",
  "Sony",
  "LG",
  "Tok&Stok",
  "Dell",
  "HP",
  "Caloi",
  "Outros",
];

const AVAILABLE_CONDITIONS = [
  "Novo",
  "Seminovo",
  "Usado",
  "Para restaurar",
];

const AVAILABLE_LOCATIONS = [
  "São Paulo, SP",
  "Rio de Janeiro, RJ",
  "Belo Horizonte, MG",
  "Brasília, DF",
  "Curitiba, PR",
  "Porto Alegre, RS",
  "Salvador, BA",
  "Fortaleza, CE",
  "Recife, PE",
  "Manaus, AM",
];

export function CustomFilterSheet({
  isOpen,
  onClose,
  customFilters,
  onCustomFilterChange,
  activeCustomFiltersCount,
  onClearCustomFilters,
}: CustomFilterSheetProps) {
  if (!isOpen) return null;

  const handleBrandToggle = (brand: string) => {
    const newBrands = customFilters.brands.includes(brand)
      ? customFilters.brands.filter((b) => b !== brand)
      : [...customFilters.brands, brand];
    onCustomFilterChange({ ...customFilters, brands: newBrands });
  };

  const handleConditionToggle = (condition: string) => {
    const newConditions = customFilters.conditions.includes(condition)
      ? customFilters.conditions.filter((c) => c !== condition)
      : [...customFilters.conditions, condition];
    onCustomFilterChange({ ...customFilters, conditions: newConditions });
  };

  const handleLocationToggle = (location: string) => {
    const newLocations = customFilters.locations.includes(location)
      ? customFilters.locations.filter((l) => l !== location)
      : [...customFilters.locations, location];
    onCustomFilterChange({ ...customFilters, locations: newLocations });
  };

  const handlePriceMinChange = (value: string) => {
    onCustomFilterChange({ ...customFilters, priceMin: value });
  };

  const handlePriceMaxChange = (value: string) => {
    onCustomFilterChange({ ...customFilters, priceMax: value });
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-card rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold">Filtros Personalizados</h2>
            {activeCustomFiltersCount > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                {activeCustomFiltersCount} {activeCustomFiltersCount === 1 ? "filtro aplicado" : "filtros aplicados"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Price Range */}
          <div>
            <h3 className="font-medium mb-4">Faixa de Preço</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Preço Mínimo
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    R$
                  </span>
                  <Input
                    type="number"
                    placeholder="0"
                    value={customFilters.priceMin}
                    onChange={(e) => handlePriceMinChange(e.target.value)}
                    className="pl-10"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Preço Máximo
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    R$
                  </span>
                  <Input
                    type="number"
                    placeholder="Ilimitado"
                    value={customFilters.priceMax}
                    onChange={(e) => handlePriceMaxChange(e.target.value)}
                    className="pl-10"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Condition Filter */}
          <div>
            <h3 className="font-medium mb-4">Condição</h3>
            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_CONDITIONS.map((condition) => {
                const isSelected = customFilters.conditions.includes(condition);
                return (
                  <button
                    key={condition}
                    onClick={() => handleConditionToggle(condition)}
                    className={`px-4 py-3 rounded-lg border-2 transition-all text-left relative ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border bg-card hover:border-primary/50"
                    }`}
                  >
                    {condition}
                    {isSelected && (
                      <Check className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Brand Filter */}
          <div>
            <h3 className="font-medium mb-4">Marca</h3>
            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_BRANDS.map((brand) => {
                const isSelected = customFilters.brands.includes(brand);
                return (
                  <button
                    key={brand}
                    onClick={() => handleBrandToggle(brand)}
                    className={`px-4 py-3 rounded-lg border-2 transition-all text-left relative ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border bg-card hover:border-primary/50"
                    }`}
                  >
                    {brand}
                    {isSelected && (
                      <Check className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location Filter */}
          <div>
            <h3 className="font-medium mb-4">Localização</h3>
            <div className="space-y-2">
              {AVAILABLE_LOCATIONS.map((location) => {
                const isSelected = customFilters.locations.includes(location);
                return (
                  <button
                    key={location}
                    onClick={() => handleLocationToggle(location)}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-all text-left relative ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border bg-card hover:border-primary/50"
                    }`}
                  >
                    {location}
                    {isSelected && (
                      <Check className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border bg-card space-y-3">
          <Button
            onClick={onClose}
            className="w-full"
          >
            Aplicar Filtros
            {activeCustomFiltersCount > 0 && ` (${activeCustomFiltersCount})`}
          </Button>
          {activeCustomFiltersCount > 0 && (
            <button
              onClick={() => {
                onClearCustomFilters();
                onClose();
              }}
              className="w-full py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Limpar Todos os Filtros
            </button>
          )}
        </div>
      </div>
    </>
  );
}